import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, Subscription, timer } from 'rxjs';
import { finalize, map, takeWhile } from 'rxjs/operators';

import {
  TWO_FACTOR_USE_CASES,
  TwoFactorUseCases
} from '@app/domain/use-cases/ptrab/two-factor/two-factor.use-cases.contract';
import {
  TwoFactorError,
  TwoFactorOperationCode,
  TwoFactorValidationResult,
  TwoFactorValidationStatus
} from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { CodeValidationDialogData } from '@app/entities/interfaces/ptrab/two-factor-dialog-data.interface';
import { Buttons } from '@app/entities/models/buttons/buttons';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { Logger, ModalManager, AlertService } from '@app/services';
import { LoadingService } from '@app/services/loading/loading.service';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { MBannerComponent } from '@mercadona/components-musi/banner';
import { MClickDirective } from '@mercadona/components-musi/common';
import { M_DIALOG_DATA } from '@mercadona/components-musi/dialog';
import { MLinkComponent } from '@mercadona/components-musi/link';
import { MTranslatePipe, MTranslateService } from '@mercadona/core/translate';
import { MIconComponent } from '@mercadona/icons';

import { CodeInputComponent } from '@/components/code-input/code-input.component';
import { PtrabTwoFactorCardComponent } from '@/components/ptrab/two-factor-card/two-factor-card.component';

@Component({
  selector: 'app-ptrab-code-validation',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    CodeInputComponent,
    MTranslatePipe,
    MBannerComponent,
    PtrabTwoFactorCardComponent,
    MLinkComponent,
    MClickDirective,
    MIconComponent
  ],
  templateUrl: './code-validation.component.html',
  styleUrls: ['./code-validation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CodeValidationComponent implements OnInit {
  readonly #data: CodeValidationDialogData = inject(M_DIALOG_DATA);
  readonly #formBuilder = inject(FormBuilder);
  readonly #loadingService = inject(LoadingService);
  readonly #alertService = inject(AlertService);
  readonly #translate = inject(MTranslateService);
  readonly #modalManager = inject(ModalManager);
  readonly #destroyRef = inject(DestroyRef);
  readonly #twoFactorUseCases: TwoFactorUseCases = inject(TWO_FACTOR_USE_CASES);
  readonly #logger = new Logger('CodeValidationFormComponent');

  readonly codeLength = 6;
  readonly defaultWaitingTime = 300;

  readonly #phoneId: string = this.#data.phoneId;
  readonly #operation: TwoFactorOperationCode = this.#data.operation;

  readonly form: FormGroup = this.#formBuilder.group({
    code: [
      '',
      Validators.compose([
        Validators.minLength(this.codeLength),
        Validators.maxLength(this.codeLength),
        Validators.required
      ])
    ]
  });
  readonly #codeControl = this.form.controls.code;

  readonly maxTriesExceeded = signal<boolean>(false);
  readonly expiredCode = signal<boolean>(false);
  readonly sendAgainAvailable = signal<boolean>(false);
  readonly timeRemaining = signal<number | null>(null);

  readonly isCodeComplete = toSignal(
    this.#codeControl.valueChanges.pipe(map((code: string | null) => code?.length === this.codeLength)),
    { initialValue: false }
  );
  readonly isCodeInvalid = toSignal(
    this.#codeControl.statusChanges.pipe(map(() => this.#codeControl.hasError('invalidCode'))),
    { initialValue: false }
  );
  readonly showCodeValidationForm = computed<boolean>(() => !this.maxTriesExceeded() && !this.expiredCode());

  readonly buttons = computed<Buttons[]>(() => {
    if (this.maxTriesExceeded()) {
      return [{ text: 'COMMON.RETURN', enabled: true, onClick: () => this.dismissBlockedUser() }];
    }

    return [
      {
        text: 'COMMON.CONFIRM',
        enabled: this.isCodeComplete() && !this.expiredCode(),
        onClick: () => this.validateCode()
      }
    ];
  });

  #timerSubscription?: Subscription;

  ngOnInit() {
    this.sendCode();
  }

  validateCode() {
    this.#loadingService.show();
    this.#twoFactorUseCases
      .validateCode(this.#operation, this.#codeControl.value, this.#phoneId)
      .pipe(finalize(() => this.#loadingService.hide()))
      .subscribe({
        next: (hash) => {
          const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.SUCCESS, hash };
          this.#modalManager.dismissMatModal(result);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === ErrorCodes.BAD_REQUEST && error.error.code) {
            this.handleTwoFactorError(error.error.code);
            return;
          }

          this.handleGenericError(error);
        }
      });
  }

  async sendCode() {
    try {
      await this.sendValidationCode();
    } catch (error) {
      this.handleGenericError(error);
      const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.ERROR };
      this.#modalManager.dismissMatModal(result);
    }
  }

  async resendCode() {
    try {
      await this.sendValidationCode();
      this.#alertService.showSuccess(
        this.#translate.translate('MODIFY_ADDRESS.CODE_SENT.TITLE'),
        this.#translate.translate('MODIFY_ADDRESS.CODE_SENT.SUBTITLE')
      );
    } catch (error) {
      this.handleGenericError(error);
    } finally {
      this.form.reset();
      this.#codeControl.setErrors(null);
      this.expiredCode.set(false);
    }
  }

  private handleTwoFactorError(error: TwoFactorError) {
    switch (error) {
      case TwoFactorError.VALIDATION_CODE_INVALID:
        this.#codeControl.setErrors({ invalidCode: true });
        break;
      case TwoFactorError.VALIDATION_CODE_MAX_TRIES_EXCEEDED:
        this.maxTriesExceeded.set(true);
        break;
      case TwoFactorError.VALIDATION_CODE_EXPIRED:
        this.expiredCode.set(true);
        break;
      default:
        this.handleGenericError(error);
    }
  }

  private handleGenericError(error: MSafeAny) {
    this.#alertService.showError(
      this.#translate.translate('UNEXPECTED.ERROR.SUBTITLE'),
      this.#translate.translate('UNEXPECTED.ERROR.TITLE')
    );
    this.#logger.error(error);
  }

  private dismissBlockedUser() {
    const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.USER_BLOCKED };
    this.#modalManager.dismissMatModal(result);
  }

  private async sendValidationCode() {
    this.#loadingService.show();
    try {
      const twoFactorResponse = await firstValueFrom(
        this.#twoFactorUseCases.sendValidationCodeToPhone(this.#operation, this.#phoneId)
      );
      this.startTimer(twoFactorResponse?.blockingTime || this.defaultWaitingTime);
    } finally {
      this.#loadingService.hide();
    }
  }

  private startTimer(waitingTime: number) {
    this.#timerSubscription?.unsubscribe();
    this.sendAgainAvailable.set(false);
    this.#timerSubscription = timer(0, 1000)
      .pipe(
        map((n) => (waitingTime - n) * 1000),
        takeWhile((n) => n > 0),
        finalize(() => this.sendAgainAvailable.set(true)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((time) => this.timeRemaining.set(time));
  }
}
